import React, { useEffect, useState } from "react";
import { Button, Spinner, Alert } from "react-bootstrap";
import { sanitizeData } from "../../MainPages/_components/Editors";
import HeroSection from "./FleetManagementComponents/HeroSection";
import FleetSection from "./FleetManagementComponents/FleetSection";
import WhyChooseUsSection from "./FleetManagementComponents/WhyChooseUsSection";
import FAQSection from "./FleetManagementComponents/FAQSection";
import FooterSection from "./FleetManagementComponents/FooterSection";

const DEFAULT_STRUCTURE = {
  heroSection: {
    isVisible: true,
    backgroundImage: "",
    heading: "",
    paragraph: "",
    imageIdentifier: "hero",
  },
  fleetSection: {
    isVisible: true,
    heading: "",
    paragraph: "",
    config: {
      showPrice: true,
      showBookNowButton: true,
      bookNowButtonText: "Book Now",
    },
  },
  whyChooseUsSection: {
    isVisible: true,
    heading: "",
    paragraph: "",
    features: [{ title: "", description: "" }],
    bgImage: "",
    illustrationImage: "",
  },
  faqSection: {
    isVisible: true,
    heading: "",
    image: "",
    faqs: [{ id: 1, title: "", description: "" }],
  },
  footerSection: {
    isVisible: true,
    heading: "",
    paragraph: "",
    contactInfo: {
      email: "",
      phone: "",
    },
    officeInfo: {
      address: "",
    },
    socialLinks: [{ icon: "FaFacebookF", link: "" }],
  },
};

const FleetManagementEditor = ({ data, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState(DEFAULT_STRUCTURE);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });

  useEffect(() => {
    if (data?.content) {
      setFormData({
        ...DEFAULT_STRUCTURE,
        ...data.content,
        heroSection: {
          ...DEFAULT_STRUCTURE.heroSection,
          ...(data.content.heroSection || {}),
        },
        fleetSection: {
          ...DEFAULT_STRUCTURE.fleetSection,
          ...(data.content.fleetSection || {}),
          config: {
            ...DEFAULT_STRUCTURE.fleetSection.config,
            ...(data.content.fleetSection?.config || {}),
          },
        },
        whyChooseUsSection: {
          ...DEFAULT_STRUCTURE.whyChooseUsSection,
          ...(data.content.whyChooseUsSection || {}),
          features:
            data.content.whyChooseUsSection?.features?.length > 0
              ? data.content.whyChooseUsSection.features
              : DEFAULT_STRUCTURE.whyChooseUsSection.features,
        },
        faqSection: {
          ...DEFAULT_STRUCTURE.faqSection,
          ...(data.content.faqSection || {}),
          faqs:
            data.content.faqSection?.faqs?.length > 0
              ? data.content.faqSection.faqs
              : DEFAULT_STRUCTURE.faqSection.faqs,
        },
        footerSection: {
          ...DEFAULT_STRUCTURE.footerSection,
          ...(data.content.footerSection || {}),
          contactInfo: {
            ...DEFAULT_STRUCTURE.footerSection.contactInfo,
            ...(data.content.footerSection?.contactInfo || {}),
          },
          officeInfo: {
            ...DEFAULT_STRUCTURE.footerSection.officeInfo,
            ...(data.content.footerSection?.officeInfo || {}),
          },
          socialLinks:
            data.content.footerSection?.socialLinks?.length > 0
              ? data.content.footerSection.socialLinks
              : DEFAULT_STRUCTURE.footerSection.socialLinks,
        },
      });
    }
  }, [data]);

  const handleSectionUpdate = (sectionName, sectionData) => {
    setFormData((prev) => ({
      ...prev,
      [sectionName]: sectionData,
    }));
  };

  const handleSave = () => {
    const sanitizedData = sanitizeData(formData, DEFAULT_STRUCTURE);
    onSave({
      pageName: data.pageName || "fleet-management",
      type: data.type || "fleet-management-content",
      content: sanitizedData,
    });
  };

  return (
    <div className="col-12">
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className="mb-3"
        >
          {alert.message}
        </Alert>
      )}

      <div className="card p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Button variant="link" className="p-0 me-3" onClick={onCancel}>
              ← Back
            </Button>
            <h4 className="m-0">Edit Fleet Management Content</h4>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <HeroSection
              data={formData.heroSection}
              onUpdate={(sectionData) =>
                handleSectionUpdate("heroSection", sectionData)
              }
            />

            <FleetSection
              data={formData.fleetSection}
              onUpdate={(sectionData) =>
                handleSectionUpdate("fleetSection", sectionData)
              }
            />

            <WhyChooseUsSection
              data={formData.whyChooseUsSection}
              onUpdate={(sectionData) =>
                handleSectionUpdate("whyChooseUsSection", sectionData)
              }
            />

            <FAQSection
              data={formData.faqSection}
              onUpdate={(sectionData) =>
                handleSectionUpdate("faqSection", sectionData)
              }
            />

            <FooterSection
              data={formData.footerSection}
              onUpdate={(sectionData) =>
                handleSectionUpdate("footerSection", sectionData)
              }
            />

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetManagementEditor;
